import { without, forEach, findIndex, get } from "lodash"
import { createReducer } from "reduxsauce"
import actionCreator from "helpers/actionCreator"
import { getUserPreference } from "helpers/userPreferences"
import { Types as LoaderTypes } from "model/data"
import { Types as MarketToolsTypes } from "modules/market-tools/model"
import { getRawSelectedViews } from "modules/factor-trends/selectors/factors"
import { createFactorViewPayload } from "modules/factor-trends-risk/helpers/tracking"
import { getSelectedModelId } from "./selectors"

export const { Types, Creators } = actionCreator("ompnt/modules/factorTrendsRisk", {
  toggle: {
    props: ["id", "currentState"],
    track: true,
    trackType: "factor-trends-graph-legend-factor",
    trackPayload: ({ id, currentState }) => createFactorViewPayload({ factor: id, state: currentState ? "on" : "off" }),
  },
  focusChartLine: ["focusedChartLine"],
  blurChartLine: [],
  setFactorCategory: {
    props: ["category"],
    track: true,
    trackType: "factor-trends-category-selection",
    trackPayload: ({ category }) => createFactorViewPayload({ type: category }),
  },
  trackCSVDownload: {
    track: true,
    trackType: "factor-trends-graph-timeseries-download",
    trackPayload: () => createFactorViewPayload(),
  },
  toggleOnly: {
    props: ["id", "only", "items"],
    track: true,
    trackType: "factor-trends-graph-legend-factor",
    trackPayload: ({ id, state }) => createFactorViewPayload({ factor: id, state }),
  },
  toggleOnlySuccess: ["selected", "only", "items"],
  addSelectedView: {
    props: ["factorId"],
    track: true,
    trackType: "factor-trends-select-view-factor-add",
    trackPayload: ({ factorId }) => createFactorViewPayload({ factorId }),
    updatePreference: {
      getItems: (state) => [{
        type: "factorTrends",
        value: { selectedViews: getRawSelectedViews(state) },
        key: getSelectedModelId(state),
      }],
    },
  },
  removeSelectedView: {
    props: ["factorId"],
    track: true,
    trackType: "factor-trends-select-view-factor-removal",
    trackPayload: ({ factorId }) => createFactorViewPayload({ factorId }),
    updatePreference: {
      getItems: (state) => [{
        type: "factorTrends",
        value: { selectedViews: getRawSelectedViews(state) },
        key: getSelectedModelId(state),
      }],
    },
  },
})

const DEFAULT_STATE = {
  category: "style",
  focusedChartLine: null,
  disabled: {},
  only: null,
  selectedViews: [],
  period: "1Y",
  date: null,
}

export const handleLoadSuccess = (state, action) => {
  if (action.resource === "factorTrendsUserPreferences") {
    const { selectedViews } = getUserPreference(action.data, "factorTrends", {})
    return { ...state, selectedViews: selectedViews || [] }
  } else {
    return state
  }
}

const handleToggle = (state, { id, currentState }) => ({
  ...state,
  focusedChartLine: currentState ? id : null,
})

function handleFocusChartLine(state, { focusedChartLine }) {
  return { ...state, focusedChartLine }
}

function handleBlurChartLine(state) {
  return { ...state, focusedChartLine: null }
}

const handleToggleOnly = (state, { id, only }) => ({
  ...state,
  focusedChartLine: only ? id : null,
})

export default createReducer(DEFAULT_STATE, {
  [Types.FOCUS_CHART_LINE]: handleFocusChartLine,
  [Types.BLUR_CHART_LINE]: handleBlurChartLine,
  [Types.TOGGLE]: handleToggle,
  [Types.TOGGLE_ONLY]: handleToggleOnly,
  [LoaderTypes.LOAD_SUCCESS]: handleLoadSuccess,
})

function handleSetFactorCategory(state, { category }) {
  return {
    ...state,
    factorCategory: category,
    only: null,
  }
}

function handleAddSelectedView(state, { factorId }) {
  return {
    ...state,
    views: [...(state.views || []), factorId],
    factorCategory: factorId,
  }
}

function handleRemoveSelectedView(state, { factorId, options }) {
  let { factorCategory } = state
  if (factorCategory === factorId) {
    const optionIndex = findIndex(options, option => option.id === factorId)
    factorCategory = get(options, `${optionIndex - 1}.id`)
  }

  return {
    ...state,
    factorCategory,
    views: without(state.views, factorId),
  }
}

function handleSetModel(state) {
  return {
    ...state,
    only: null,
    disabled: [],
  }
}

const handleToggleQS = (state, { id, currentState }) => {
  return {
    ...state,
    only: null,
    disabled: currentState ? without(state.disabled, id) : [...(state.disabled || []), id],
  }
}

const handleToggleOnlyQS = (state, { id, only, items }) => {
  const disabled = []
  let onlyDisplay = null

  if (only) {
    onlyDisplay = id
    forEach(items, item => {
      if (item.id !== id) {
        disabled.push(item.id)
      }
    })
  }

  return { ...state, disabled, only: onlyDisplay }
}

export const queryStringReducers = {
  [Types.SET_FACTOR_CATEGORY]: handleSetFactorCategory,
  [Types.ADD_SELECTED_VIEW]: handleAddSelectedView,
  [Types.REMOVE_SELECTED_VIEW]: handleRemoveSelectedView,
  [Types.TOGGLE]: handleToggleQS,
  [Types.TOGGLE_ONLY]: handleToggleOnlyQS,
  [MarketToolsTypes.SET_MODEL]: handleSetModel,
}
